import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Divider
} from '@mui/material';
import {
  CalendarToday,
  Euro,
  CheckCircle,
} from '@mui/icons-material';
import apiClient from '../config/axios';

const MembershipListSelection = ({ open, onClose, onConfirm }) => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(null);

  useEffect(() => {
    if (open) {
      fetchMemberships();
    }
  }, [open]);

  useEffect(() => {
    // If there's only one membership available, auto-select it
    if (memberships.length === 1 && !selectedMembership) {
      setSelectedMembership(memberships[0]);
    }
  }, [memberships, selectedMembership]);

  const fetchMemberships = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/membership-list', {
        params: {
          status: 'published',
          minAmount: 1,
        }
      });
      
      // Filter memberships with endDate > now
      const now = new Date();
      const availableMemberships = response.data.data.filter(membership => 
        new Date(membership.endDate) > now
      );
      
      setMemberships(availableMemberships);
    } catch (error) {
      setError('Errore nel caricamento dei tesseramenti disponibili');
      console.error('Error fetching memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipSelect = (membership) => {
    setSelectedMembership(membership);
  };

  const handleConfirm = () => {
    if (selectedMembership) {
      onConfirm(selectedMembership);
    }
  };

  const handleClose = () => {
    setSelectedMembership(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Seleziona Tesseramento
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : memberships.length === 0 ? (
          <Alert severity="info">
            Nessun tesseramento disponibile al momento.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {memberships.map((membership) => (
              <Grid size={{ xs: 12, md: 6 }} offset={{ xs: 0, md: 3 }} key={membership._id}>
                <Card 
                  variant={selectedMembership?._id === membership._id ? "elevation" : "outlined"}
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedMembership?._id === membership._id ? 2 : 1,
                    borderColor: selectedMembership?._id === membership._id ? 'primary.main' : 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => handleMembershipSelect(membership)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="div">
                        {membership.name}
                      </Typography>
                      <Chip 
                        label={`${membership.amount}`}
                        color="primary"
                        icon={<Euro />}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {membership.description}
                    </Typography>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(membership.startDate).toLocaleDateString('it-IT')} - {new Date(membership.endDate).toLocaleDateString('it-IT')}
                        </Typography>
                      </Box>
                      
                      {selectedMembership?._id === membership._id && (
                        <CheckCircle color="primary" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Annulla
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedMembership}
        >
          Conferma Selezione
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MembershipListSelection; 